import { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor } from '../state/store';
import { TOOLS } from '../tools/registry';

export interface Command {
  id: string;
  title: string;
  hint?: string;
  run: () => void;
}

/** Builds the command list from live store actions, so nothing here is inert. */
export function useCommands(open: (dialog: string) => void): Command[] {
  return useMemo(() => {
    const s = () => useEditor.getState();
    const commands: Command[] = [
      { id: 'new', title: 'New document', hint: 'Ctrl+N', run: () => open('new') },
      { id: 'export', title: 'Export ANSI', hint: 'Ctrl+E', run: () => open('export') },
      { id: 'preview', title: 'Preview in terminal', hint: 'Ctrl+P', run: () => open('preview') },
      { id: 'templates', title: 'Insert template', run: () => open('templates') },
      { id: 'image', title: 'Image → ANSI', run: () => open('image') },
      { id: 'search', title: 'Search and replace', hint: 'Ctrl+F', run: () => open('search') },
      { id: 'undo', title: 'Undo', hint: 'Ctrl+Z', run: () => s().undo() },
      { id: 'redo', title: 'Redo', hint: 'Ctrl+Shift+Z', run: () => s().redo() },
      { id: 'select-all', title: 'Select all', hint: 'Ctrl+A', run: () => s().selectAll() },
      { id: 'select-none', title: 'Deselect', hint: 'Esc', run: () => s().selectNone() },
      { id: 'invert', title: 'Invert selection', run: () => s().invertSelection() },
      { id: 'copy', title: 'Copy selection', hint: 'Ctrl+C', run: () => s().copySelection() },
      { id: 'paste', title: 'Paste', hint: 'Ctrl+V', run: () => s().paste() },
      { id: 'delete', title: 'Delete selection', hint: 'Del', run: () => s().deleteSelection() },
      { id: 'flip-h', title: 'Flip selection horizontally', run: () => s().flipSelection('h') },
      { id: 'flip-v', title: 'Flip selection vertically', run: () => s().flipSelection('v') },
      { id: 'fill-selection', title: 'Fill selection with brush character', run: () => s().fillSelection() },
      { id: 'gradient-selection', title: 'Apply gradient to selection', run: () => s().applyGradientToSelection() },
      { id: 'clear-colors', title: 'Clear colours in selection', run: () => s().clearSelectionColors() },
      { id: 'select-by-char', title: 'Select by character', run: () => s().selectByChar(s().brush.char) },
      { id: 'select-by-fg', title: 'Select by foreground colour', run: () => s().selectByColor(s().brush.fg, 'fg') },
      { id: 'anim-new', title: 'Animation: create', run: () => s().createAnimation() },
      { id: 'anim-frame', title: 'Animation: add frame on a new layer', run: () => s().addFrameWithLayer() },
      { id: 'anim-capture', title: 'Animation: capture current layers as a frame', run: () => s().captureFrame() },
      {
        id: 'anim-play',
        title: 'Animation: play / pause',
        run: () => (s().playing ? s().pauseAnimation() : s().playAnimation()),
      },
      { id: 'anim-next', title: 'Animation: next frame', run: () => s().stepFrame(1) },
      { id: 'anim-prev', title: 'Animation: previous frame', run: () => s().stepFrame(-1) },
      { id: 'add-layer', title: 'Add layer', run: () => s().addLayer() },
      { id: 'merge-layer', title: 'Merge layer down', run: () => s().mergeLayerDown(s().active().doc.activeLayerId) },
      {
        id: 'toggle-grid',
        title: 'Toggle grid',
        run: () => s().setRenderOptions({ showGrid: !s().renderOptions.showGrid }),
      },
      {
        id: 'toggle-rulers',
        title: 'Toggle rulers',
        run: () => s().setRenderOptions({ showRulers: !s().renderOptions.showRulers }),
      },
      {
        id: 'zoom-reset',
        title: 'Reset zoom',
        run: () => s().setRenderOptions({ zoom: 1 }),
      },
      {
        id: 'preview-16',
        title: 'Preview as ANSI 16',
        run: () => s().setRenderOptions({ previewMode: 'ansi16' }),
      },
      {
        id: 'preview-256',
        title: 'Preview as ANSI 256',
        run: () => s().setRenderOptions({ previewMode: 'ansi256' }),
      },
      {
        id: 'preview-true',
        title: 'Preview as TrueColor',
        run: () => s().setRenderOptions({ previewMode: 'truecolor' }),
      },
      {
        id: 'clear-guides',
        title: 'Clear guides',
        run: () => s().clearGuides(),
      },
    ];

    for (const tool of TOOLS) {
      commands.push({
        id: `tool-${tool.id}`,
        title: `Tool: ${tool.name}`,
        hint: tool.shortcut,
        run: () => s().setTool(tool.id),
      });
    }
    return commands;
  }, [open]);
}

export function CommandPalette({
  commands,
  onClose,
}: {
  commands: Command[];
  onClose: () => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands
      .map((c) => {
        const title = c.title.toLowerCase();
        const score = title.startsWith(q) ? 3 : title.includes(q) ? 2 : fuzzy(title, q) ? 1 : 0;
        return { c, score };
      })
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((r) => r.c);
  }, [commands, query]);

  const clamped = Math.min(index, Math.max(0, filtered.length - 1));

  return (
    <div className="cmdk" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cmdk-box">
        <input
          ref={inputRef}
          className="cmdk-input"
          placeholder="Type a command…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIndex(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setIndex(Math.min(clamped + 1, filtered.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setIndex(Math.max(clamped - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const cmd = filtered[clamped];
              if (cmd) {
                onClose();
                cmd.run();
              }
            } else if (e.key === 'Escape') {
              onClose();
            }
          }}
        />
        <div className="cmdk-list">
          {filtered.map((c, i) => (
            <div
              key={c.id}
              className={`cmdk-item${i === clamped ? ' active' : ''}`}
              onMouseEnter={() => setIndex(i)}
              onClick={() => {
                onClose();
                c.run();
              }}
            >
              <span>{c.title}</span>
              {c.hint ? <span className="hint">{c.hint}</span> : null}
            </div>
          ))}
          {filtered.length === 0 ? <div className="empty-state">No matching command.</div> : null}
        </div>
      </div>
    </div>
  );
}

function fuzzy(haystack: string, needle: string): boolean {
  let i = 0;
  for (const ch of haystack) {
    if (ch === needle[i]) i += 1;
    if (i === needle.length) return true;
  }
  return false;
}
